import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import './App.css';
import { OcrResult, QueueItem, FileGroup } from './types';
import { uid, savePinnedResults, loadPinnedResults, clearPinnedStorage, migrateFromLocalStorage } from './utils';
import { FileCard } from './components/FileCard';

/* ───── App ───── */
function App() {
  const [results, setResults] = useState<OcrResult[]>([]);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set());
  const [pinnedGroups, setPinnedGroups] = useState<Set<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);
  const [orderId, setOrderId] = useState('638fbbd0-9d19-4136-8055-a256ebf48ce9');
  const [orderHash, setOrderHash] = useState('a2e7610873de3699e36b2bc404680c4a516cb154b904c590e63d2f843e4c2608');
  const processingRef = useRef(false);
  const cancelledRef = useRef<Set<string>>(new Set());
  const queueRef = useRef<QueueItem[]>([]);
  const pinnedLoadedRef = useRef(false);

  // Load pinned results from IndexedDB on mount (+ migrate from localStorage)
  useEffect(() => {
    (async () => {
      // One-time migration from localStorage
      const migrated = migrateFromLocalStorage();
      if (migrated) {
        const results = migrated.map((r: any) => ({ ...r, timestamp: new Date(r.timestamp) }));
        const pinnedIds = new Set<string>(results.map((r: any) => r.fileGroupId));
        setResults(prev => [...results, ...prev]);
        setPinnedGroups(pinnedIds);
        await savePinnedResults(results, pinnedIds);
        pinnedLoadedRef.current = true;
        return;
      }

      const { results: pinned, pinnedIds } = await loadPinnedResults();
      if (pinned.length > 0) {
        setResults(prev => [...pinned, ...prev]);
        setPinnedGroups(pinnedIds);
      }
      pinnedLoadedRef.current = true;
    })();
  }, []);

  // Sync pinned results to IndexedDB
  useEffect(() => {
    if (!pinnedLoadedRef.current) return; // skip until initial load is done
    if (pinnedGroups.size === 0) {
      clearPinnedStorage();
      return;
    }
    savePinnedResults(results, pinnedGroups);
  }, [pinnedGroups, results]);

  const [newResultGroups, setNewResultGroups] = useState<Set<string>>(new Set());

  const toggleFile = (fileGroupId: string) => {
    setExpandedFiles(prev => {
      const next = new Set(prev);
      if (next.has(fileGroupId)) next.delete(fileGroupId);
      else next.add(fileGroupId);
      return next;
    });
    // Clear "new" indicator when user opens the accordion
    setNewResultGroups(prev => {
      if (!prev.has(fileGroupId)) return prev;
      const next = new Set(prev);
      next.delete(fileGroupId);
      return next;
    });
  };

  const toggleRun = (runId: string) => {
    setExpandedRuns(prev => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });
  };

  const togglePin = (fileGroupId: string) => {
    setPinnedGroups(prev => {
      const next = new Set(prev);
      if (next.has(fileGroupId)) next.delete(fileGroupId);
      else next.add(fileGroupId);
      return next;
    });
  };

  const removeResult = (id: string) => {
    setResults(prev => {
      const filtered = prev.filter(r => r.id !== id);
      const removed = prev.find(r => r.id === id);
      if (removed) {
        const groupStillHasRuns = filtered.some(r => r.fileGroupId === removed.fileGroupId);
        if (!groupStillHasRuns) {
          setExpandedFiles(ef => {
            const next = new Set(ef);
            next.delete(removed.fileGroupId);
            return next;
          });
          setPinnedGroups(pp => {
            const next = new Set(pp);
            next.delete(removed.fileGroupId);
            return next;
          });
        }
      }
      return filtered;
    });
    setExpandedRuns(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const removeFileGroup = (fileGroupId: string) => {
    setResults(prev => prev.filter(r => r.fileGroupId !== fileGroupId));
    setExpandedFiles(prev => {
      const next = new Set(prev);
      next.delete(fileGroupId);
      return next;
    });
    setExpandedRuns(prev => {
      const next = new Set(prev);
      results.filter(r => r.fileGroupId === fileGroupId).forEach(r => next.delete(r.id));
      return next;
    });
    setPinnedGroups(prev => {
      const next = new Set(prev);
      next.delete(fileGroupId);
      return next;
    });
  };

  const clearAll = () => {
    setResults([]);
    setExpandedFiles(new Set());
    setExpandedRuns(new Set());
    setPinnedGroups(new Set());
    clearPinnedStorage();
  };

  const cancelQueueItem = (id: string) => {
    cancelledRef.current.add(id);
    const item = queueRef.current.find(q => q.id === id);
    if (item) { item.status = 'cancelled'; }
    flushQueue();
  };

  const flushQueue = useCallback(() => {
    setQueue([...queueRef.current]);
  }, []);

  const processFiles = useCallback(async (files: File[], existingGroupId?: string) => {
    const isRerun = !!existingGroupId;
    const newItems: QueueItem[] = files.map(f => ({
      id: uid(),
      file: f,
      fileGroupId: existingGroupId || uid(),
      status: 'waiting' as const,
      progress: 0,
      isRerun,
    }));

    queueRef.current = [...queueRef.current, ...newItems];
    flushQueue();

    if (processingRef.current) return;
    processingRef.current = true;

    const processNext = async (): Promise<void> => {
      const next = queueRef.current.find(q => q.status === 'waiting');
      if (!next) {
        processingRef.current = false;
        queueRef.current = queueRef.current.filter(q => q.status !== 'done' && q.status !== 'cancelled');
        flushQueue();
        return;
      }

      if (cancelledRef.current.has(next.id)) {
        cancelledRef.current.delete(next.id);
        return processNext();
      }

      next.status = 'processing';
      next.progress = 10;
      flushQueue();

      // Simulate progress: 10% -> ~90% over ~12s
      const progressInterval = setInterval(() => {
        if (next.progress < 90) {
          next.progress += (90 - next.progress) * 0.08;
          flushQueue();
        }
      }, 500);

      try {
        const formData = new FormData();
        formData.append('file', next.file);
        formData.append('orderId', orderId);
        formData.append('orderHash', orderHash);

        const response = await fetch('/api/ocr/compare', {
          method: 'POST',
          body: formData,
        });
        clearInterval(progressInterval);

        if (cancelledRef.current.has(next.id)) {
          cancelledRef.current.delete(next.id);
          next.status = 'cancelled';
          next.progress = 0;
          flushQueue();
          return processNext();
        }

        if (!response.ok) throw new Error(`Server error: ${response.status}`);
        const data = await response.json();

        const fileBase64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const r = reader.result as string;
            resolve(r.split(',')[1]);
          };
          reader.readAsDataURL(next.file);
        });

        const ocrResult: OcrResult = {
          id: next.id,
          fileGroupId: next.fileGroupId,
          fileName: data.fileName,
          fileSize: data.fileSize,
          mimeType: data.mimeType,
          fileBlob: fileBase64,
          documentAi: data.documentAi,
          deapOcr: data.deapOcr,
          timestamp: new Date(),
        };

        setResults(prev => [ocrResult, ...prev]);
        if (next.isRerun) {
          setNewResultGroups(prev => new Set(prev).add(ocrResult.fileGroupId));
        } else {
          setExpandedFiles(prev => new Set(prev).add(ocrResult.fileGroupId));
          setExpandedRuns(prev => new Set(prev).add(ocrResult.id));
        }
        next.status = 'done';
        next.progress = 100;
        flushQueue();
      } catch (err: any) {
        clearInterval(progressInterval);
        next.status = 'done';
        next.progress = 100;
        flushQueue();
        const ocrResult: OcrResult = {
          id: next.id,
          fileGroupId: next.fileGroupId,
          fileName: next.file.name,
          fileSize: next.file.size,
          mimeType: next.file.type,
          fileBlob: '',
          documentAi: { error: err.message },
          deapOcr: { error: err.message },
          timestamp: new Date(),
        };
        setResults(prev => [ocrResult, ...prev]);
        if (next.isRerun) {
          setNewResultGroups(prev => new Set(prev).add(ocrResult.fileGroupId));
        } else {
          setExpandedFiles(prev => new Set(prev).add(ocrResult.fileGroupId));
          setExpandedRuns(prev => new Set(prev).add(ocrResult.id));
        }
      }

      return processNext();
    };

    processNext();
  }, [flushQueue]);

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    processFiles(Array.from(fileList));
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [processFiles]);

  const activeQueue = queue.filter(q => (q.status === 'waiting' || q.status === 'processing') && !q.isRerun);

  const rerunningGroups = useMemo(() => {
    const set = new Set<string>();
    queue.forEach(q => {
      if (q.isRerun && (q.status === 'waiting' || q.status === 'processing')) {
        set.add(q.fileGroupId);
      }
    });
    return set;
  }, [queue]);

  // Group results by fileGroupId, pinned groups first
  const fileGroups = useMemo((): FileGroup[] => {
    const groupMap = new Map<string, OcrResult[]>();
    const groupOrder: string[] = [];

    for (const r of results) {
      if (!groupMap.has(r.fileGroupId)) {
        groupMap.set(r.fileGroupId, []);
        groupOrder.push(r.fileGroupId);
      }
      groupMap.get(r.fileGroupId)!.push(r);
    }

    const groups = groupOrder.map(gid => ({
      fileGroupId: gid,
      fileName: groupMap.get(gid)![0].fileName,
      runs: groupMap.get(gid)!,
    }));

    return groups;
  }, [results]);

  return (
    <>
      {/* Header */}
      <header className="header">
        <div className="header-inner">
          <div className="header-title">
            <h1>OCR Comparator</h1>
            <span className="header-subtitle">Porovnani GCP Document AI vs DEAP OCR</span>
          </div>
          {results.length > 0 && (
            <button className="btn-clear-all" onClick={clearAll}>
              Clear All Results
            </button>
          )}
        </div>
      </header>

    <div className="app">
      <div className="body-wrap">
        {/* DEAP OCR Config */}
        <div className="config-row">
          <div className="config-field">
            <label className="config-label">Order ID</label>
            <input
              className="config-input"
              type="text"
              value={orderId}
              onChange={e => setOrderId(e.target.value)}
              placeholder="orderId"
            />
          </div>
          <div className="config-field">
            <label className="config-label">Order Hash</label>
            <input
              className="config-input"
              type="text"
              value={orderHash}
              onChange={e => setOrderHash(e.target.value)}
              placeholder="orderHash"
            />
          </div>
        </div>

        {/* Upload Zone */}
        <div
          className={`upload-zone ${dragOver ? 'drag-over' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          <input
            id="file-input"
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.tiff"
            multiple
            onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
            hidden
          />
          <svg className="upload-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#58A6FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
            <path d="M12 12v9" />
            <path d="m16 16-4-4-4 4" />
          </svg>
          <span className="upload-title">Drag & drop PDF files here, or click to browse</span>
          <span className="upload-helper">Supports PDF files up to 50 MB each</span>
        </div>

        {/* Processing Queue */}
        {activeQueue.length > 0 && (
          <div className="queue-box">
            {activeQueue.map((item, idx) => (
              <div
                key={item.id}
                className={`queue-row ${idx === 0 ? 'first' : ''} ${idx === activeQueue.length - 1 ? 'last' : ''}`}
              >
                <span className="queue-txt">
                  {item.status === 'processing' ? `Processing: ${item.file.name}` : `Queued: ${item.file.name}`}
                </span>
                <div className="queue-right">
                  {item.status === 'processing' ? (
                    <div className="progress-bg">
                      <div className="progress-fill" style={{ width: `${item.progress}%` }} />
                    </div>
                  ) : (
                    <span className="queue-waiting">Waiting...</span>
                  )}
                  <button className="queue-cancel" onClick={(e) => { e.stopPropagation(); cancelQueueItem(item.id); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F85149" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* File Group Cards */}
        <div className="results-area">
          {fileGroups.map(group => (
            <FileCard
              key={group.fileGroupId}
              group={group}
              expandedFiles={expandedFiles}
              expandedRuns={expandedRuns}
              isPinned={pinnedGroups.has(group.fileGroupId)}
              isProcessing={rerunningGroups.has(group.fileGroupId)}
              hasNewResults={newResultGroups.has(group.fileGroupId)}
              onToggleFile={() => toggleFile(group.fileGroupId)}
              onToggleRun={toggleRun}
              onTogglePin={() => togglePin(group.fileGroupId)}
              onRemoveRun={removeResult}
              onRemoveGroup={() => removeFileGroup(group.fileGroupId)}
              onRerun={(fileGroupId) => {
                const latestRun = group.runs[0];
                if (!latestRun.fileBlob) return;
                const byteChars = atob(latestRun.fileBlob);
                const byteArr = new Uint8Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
                const file = new File([byteArr], latestRun.fileName, { type: latestRun.mimeType });
                processFiles([file], fileGroupId);
              }}
            />
          ))}
        </div>
      </div>
    </div>
    </>
  );
}

export default App;
